import React from 'react';
import { useImmer } from 'use-immer';
import { Row, Col } from 'react-bootstrap';
import {
  Modal,
  ModalTitle,
  ModalContent,
  ModalFooter,
  TextInput,
  TextArea,
} from '@utahdts/utah-design-system';
import { numbersUpToTwoDecimalPlaces, validateField } from '../../../../reuseableComponents/forms/formValidation';
import { formatNumber } from '../../dwriUtils';
import '../../dwri.css';

const initialFormState = {
  selectedDemandValue: '',
  adjustedValue: '',
  adjustedError: '',
  changeJustificationValue: '',
  changeJustificationError: '',
};

const SelectedDemandTable = ({
  valueLabel,
  adjustedLabel,
  value,
  adjustedValue,
  justification,
  canEdit,
  onSave,
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [formState, setFormState] = useImmer(initialFormState);
  const [mutationError, setMutationError] = React.useState('');

  function handleEditClick() {
    setFormState({
      ...initialFormState,
      selectedDemandValue: value == null ? '' : String(value),
      adjustedValue: adjustedValue == null ? '' : String(adjustedValue),
      changeJustificationValue: justification ?? '',
    });
    setMutationError('');
    setIsEditing(true);
  }

  function handleModalClose() {
    setIsEditing(false);
    setFormState(initialFormState);
    setMutationError('');
  }

  function isJustificationRequired() {
    return formState.adjustedValue !== '';
  }

  async function handleSave() {
    const errors = {};
    validateField(errors, formState, 'adjustedValue', (val) => val === '' || numbersUpToTwoDecimalPlaces(val) === true, 'Must be a number with up to 2 decimal places');
    validateField(errors, formState, 'changeJustificationValue', (val) => !isJustificationRequired() || !!val.trim(), `Change Justification is required when setting ${adjustedLabel}.`);

    if (Object.keys(errors).length > 0) {
      setFormState((draft) => {
        draft.adjustedError = errors.adjustedValue?.message || '';
        draft.changeJustificationError = errors.changeJustificationValue?.message || '';
      });
      return;
    }

    setMutationError('');
    try {
      await onSave({
        adjustedValue: formState.adjustedValue === '' ? null : Number(formState.adjustedValue),
        justification: formState.changeJustificationValue,
      });
      handleModalClose();
    } catch (mutationErr) {
      setMutationError(mutationErr.message || `Failed to update ${adjustedLabel}.`);
    }
  }

  return (
    <>
      <div className="table-responsive">
        <Row className={`dwri-erc-table-header dwri-selected-demand-header gx-0 ${canEdit ? 'has-edit' : ''}`}>
          <Col className="dwri-table-header-cell"><span><b>{valueLabel}</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>{adjustedLabel}</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Change Justification</b></span></Col>
          {canEdit && (
            <Col className="dwri-table-header-cell dwri-col-edit"><span><b>Edit</b></span></Col>
          )}
        </Row>
        <Row className={`dwri-erc-table-row dwri-selected-demand-row gx-0 ${canEdit ? 'has-edit' : ''} is-even`}>
          <Col className="dwri-table-cell"><span>{formatNumber(value)}</span></Col>
          <Col className="dwri-table-cell"><span>{formatNumber(adjustedValue)}</span></Col>
          <Col className="dwri-table-cell"><span>{justification}</span></Col>
          {canEdit && (
            <Col className="dwri-table-cell dwri-col-edit">
              <button
                className="link-button"
                aria-label={`Edit ${valueLabel}`}
                type="button"
                onClick={handleEditClick}
              >
                Edit
              </button>
            </Col>
          )}
        </Row>
      </div>

      {isEditing && (
        <Modal
          onClose={handleModalClose}
          onEscape={handleModalClose}
        >
          <ModalTitle>Update {valueLabel}</ModalTitle>
          <ModalContent>
            <div className="dwri-peak-day-demand-modal-grid">
              <div className="dwri-peak-day-demand-modal-row dwri-peak-day-demand-modal-row--two">
                <TextInput label={valueLabel} value={formState.selectedDemandValue} isDisabled />
                <TextInput
                  label={adjustedLabel}
                  value={formState.adjustedValue}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    const validationResult = numbersUpToTwoDecimalPlaces(nextValue);
                    if (validationResult === true) {
                      setFormState((draft) => {
                        draft.adjustedValue = nextValue;
                        draft.adjustedError = '';
                      });
                    } else {
                      setFormState((draft) => { draft.adjustedError = validationResult; });
                    }
                  }}
                  onBlur={() => {
                    const validationResult = numbersUpToTwoDecimalPlaces(formState.adjustedValue);
                    setFormState((draft) => {
                      draft.adjustedError = validationResult === true ? '' : validationResult;
                    });
                  }}
                  errorMessage={formState.adjustedError || undefined}
                />
              </div>
              <div className="dwri-peak-day-demand-modal-row dwri-peak-day-demand-modal-row--full">
                <TextArea
                  label="Change Justification"
                  value={formState.changeJustificationValue}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setFormState((draft) => {
                      draft.changeJustificationValue = nextValue;
                      draft.changeJustificationError = '';
                    });
                  }}
                  isRequired={isJustificationRequired()}
                  errorMessage={formState.changeJustificationError || undefined}
                />
              </div>
            </div>
          </ModalContent>
          <ModalFooter className="dwri-service-modal-footer">
            {mutationError && (
              <div className="text-danger">{mutationError}</div>
            )}
            <button type="button" onClick={handleModalClose}>Close</button>
            <button type="button" onClick={handleSave}>Save</button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default SelectedDemandTable;
